import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money, imgSrc } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const BagLine = ({ item }) => {
  const { update, remove } = useCart();
  const id = `${item.product_id}-${item.size}`;
  return (
    <li data-testid={`cart-item-${id}`} className="flex gap-4 border-b border-line py-5">
      <img src={imgSrc(item.image)} alt={item.name} className="h-28 w-22 w-[88px] object-cover" />
      <div className="flex flex-1 flex-col">
        <div className="flex justify-between gap-2">
          <p className="font-display text-xl leading-tight">{item.name}</p>
          <button data-testid={`cart-remove-${id}`} onClick={() => remove(item.product_id, item.size)} aria-label="Remove"><X className="h-4 w-4" /></button>
        </div>
        <p className="eyebrow mt-1">Size {item.size}</p>
        <div className="mt-auto flex items-center justify-between">
          <div className="flex items-center border border-line">
            <button data-testid={`cart-dec-${id}`} className="p-2 hover:bg-sand" onClick={() => update(item.product_id, item.size, item.quantity - 1)}><Minus className="h-3 w-3" /></button>
            <span className="w-8 text-center font-mono text-sm">{item.quantity}</span>
            <button data-testid={`cart-inc-${id}`} className="p-2 hover:bg-sand" onClick={() => update(item.product_id, item.size, item.quantity + 1)}><Plus className="h-3 w-3" /></button>
          </div>
          <span className="font-mono text-sm">{money(item.price * item.quantity)}</span>
        </div>
      </div>
    </li>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal } = useCart();
  const navigate = useNavigate();
  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[80]">
          <motion.div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
          <motion.aside
            data-testid="cart-drawer"
            data-lenis-prevent
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-bone"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="flex items-center justify-between border-b border-line px-6 py-5">
              <h2 className="font-display text-3xl italic">Your bag</h2>
              <button data-testid="cart-close-button" onClick={() => setOpen(false)} aria-label="Close bag"><X className="h-6 w-6" /></button>
            </div>
            {items.length === 0 ? (
              <div data-testid="cart-empty" className="flex flex-1 flex-col items-start justify-center gap-6 px-6">
                <p className="font-display text-4xl leading-tight">Nothing here yet.<br /><em className="text-stone">Start with a coat.</em></p>
                <Link to="/shop" onClick={() => setOpen(false)} data-testid="cart-shop-link" className="rounded-full bg-ink px-6 py-3 text-sm text-bone">Browse the collection</Link>
              </div>
            ) : (
              <>
                <ul className="flex-1 overflow-y-auto px-6">{items.map((it) => <BagLine key={`${it.product_id}-${it.size}`} item={it} />)}</ul>
                <div className="border-t border-line px-6 py-6">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal" className="font-mono">{money(subtotal)}</span>
                  </div>
                  <p className="mt-1 text-xs text-stone">Shipping and taxes calculated at payment.</p>
                  <button
                    data-testid="cart-checkout-button"
                    onClick={() => { setOpen(false); navigate("/checkout"); }}
                    className="mt-5 w-full rounded-full bg-ink py-4 text-sm tracking-wide text-bone transition-colors duration-300 hover:bg-signal"
                  >
                    Checkout
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};
